import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import { SEOHead } from "@/components/SEOHead";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Link } from "react-router-dom";
import { Award, Users, Calendar, ExternalLink, Linkedin } from "lucide-react";
import { useTranslation } from "react-i18next";
import founderPhoto from "@/assets/founder-photo.png";

const About = () => {
  const { t } = useTranslation();
  
  const jsonLd = [{
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "NoQ5.ai",
    "url": "https://noq5.ai",
    "logo": "https://noq5.ai/og-image-noq5.png",
    "foundingDate": "2025",
    "founders": [{
      "@type": "Person",
      "name": "Batiste Pasquet",
      "jobTitle": "CEO & Founder",
      "description": "20+ years sales experience at LinkedIn and DocuSign, former top global sales rep with multiple awards",
      "sameAs": "https://www.linkedin.com/in/batistepasquet/"
    }],
    "employees": [{
      "@type": "Person",
      "name": "Isoline Marchand",
      "jobTitle": "CMO",
      "sameAs": "https://www.linkedin.com/in/isoline-marchand/"
    }],
    "description": "NoQ5.ai is the AI-Powered Revenue Intelligence Platform for HubSpot, built by sales professionals who understand CRM friction.",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "IE"
    }
  }];

  return (
    <Layout>
      <SEOHead
        title="About NoQ5 | Founded by Ex-LinkedIn & DocuSign Leader"
        description="NoQ5 was founded by Batiste Pasquet, ex-LinkedIn and DocuSign sales leader. EU-hosted, GDPR-compliant AI Chief of Staff for revenue teams."
        keywords="NoQ5.ai founder, Batiste Pasquet, LinkedIn sales leader, DocuSign, Web Summit 2025, GDPR compliant AI, sales AI startup Ireland, HubSpot AI"
        canonicalUrl="https://noq5.ai/about"
        jsonLd={jsonLd}
      />

      <Breadcrumbs items={[{ label: "About", href: "/about" }]} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-background" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              {`${t('about.heroTitleLine1')} ${t('about.heroTitleLine2')}`}
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {t('about.heroSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-card rounded-sm p-8 md:p-12 border border-border shadow-lg">
              <div className="flex items-start gap-6 mb-8">
                <div className="flex-shrink-0">
                  <Avatar className="w-24 h-24 border-2 border-primary/20">
                    <AvatarImage src={founderPhoto} alt="Portrait of Batiste Pasquet, NoQ5.ai founder and former LinkedIn and DocuSign sales leader" />
                    <AvatarFallback>BP</AvatarFallback>
                  </Avatar>
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-2">{t('about.founderTitle')}</h2>
                  <p className="text-primary font-semibold">{t('about.founderRole')}</p>
                  <a 
                    href="https://www.linkedin.com/in/batistepasquet/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mt-2"
                  >
                    <Linkedin className="w-4 h-4" />
                    LinkedIn Profile
                  </a>
                </div>
              </div>
              
              <div className="space-y-6 text-lg text-foreground/90">
                <p>{t('about.founderIntro')}</p>
                <p>
                  {t('about.founderFrustrationStart')}
                  <span className="font-semibold text-primary">{t('about.founderFrustrationHighlight')}</span>
                  {t('about.founderFrustrationEnd')}
                </p>
                <p>{t('about.founderProblem')}</p>
                <p className="font-semibold">{t('about.founderSolution')}</p>
                <p>
                  {t('about.founderVisionStart')}
                  <span className="text-primary font-semibold">{t('about.founderVisionHighlight')}</span>
                  {t('about.founderVisionEnd')}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mt-12 pt-8 border-t border-border">
                <div className="text-center">
                  <Award className="w-8 h-8 text-primary mx-auto mb-3" />
                  <p className="text-2xl font-bold">{t('about.stats.years')}</p>
                  <p className="text-sm text-muted-foreground">{t('about.stats.yearsLabel')}</p>
                </div>
                <div className="text-center">
                  <Award className="w-8 h-8 text-primary mx-auto mb-3" />
                  <p className="text-2xl font-bold">{t('about.stats.topRep')}</p>
                  <p className="text-sm text-muted-foreground">{t('about.stats.topRepLabel')}</p>
                </div>
                <div className="text-center">
                  <Award className="w-8 h-8 text-primary mx-auto mb-3" />
                  <p className="text-2xl font-bold">{t('about.stats.awards')}</p>
                  <p className="text-sm text-muted-foreground">{t('about.stats.awardsLabel')}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('about.companyTitle')}</h2>
              <p className="text-xl text-muted-foreground">{t('about.companySubtitle')}</p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-card rounded-sm p-8 border border-border"
              >
                <Calendar className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">{t('about.founded')}</h3>
                <p className="text-muted-foreground">{t('about.foundedDesc')}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-card rounded-sm p-8 border border-border"
              >
                <Users className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">{t('about.platformReady')}</h3>
                <p className="text-muted-foreground">{t('about.platformReadyDesc')}</p>
              </motion.div>
            </div>

            {/* Recognition Section */}
            <div className="mt-12 space-y-6">
              {/* Web Summit Recognition */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-gradient-to-r from-primary/10 via-primary/5 to-background rounded-sm p-8 md:p-12 border border-primary/20"
              >
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-shrink-0">
                    <Award className="w-16 h-16 text-primary" />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-2xl font-bold mb-3">{t('about.webSummitTitle')}</h3>
                    <p className="text-lg text-muted-foreground mb-4">{t('about.webSummitDesc')}</p>
                    <Button asChild variant="outline" size="lg">
                      <a 
                        href="https://websummit.com/appearances/lis25/9e71fc43-9124-4b1f-9709-0e052f13fd49/noq5.ai?utm_source=sharing&utm_campaign=social-cards&utm_medium=url"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2"
                      >
                        {t('about.webSummitButton')}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                </div>
              </motion.div>

              {/* Next Wave Recognition */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-gradient-to-r from-primary/10 via-primary/5 to-background rounded-sm p-8 md:p-12 border border-primary/20"
              >
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-shrink-0">
                    <Award className="w-16 h-16 text-primary" />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-2xl font-bold mb-3">{t('about.nextWaveTitle')}</h3>
                    <p className="text-lg text-muted-foreground mb-4">
                      {t('about.nextWaveDesc')}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button asChild variant="outline" size="lg">
                        <a 
                          href="https://www.enterprise-ireland.com/en/supports/next-wave"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2"
                        >
                          {t('about.learnMoreNextWave')}
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                      <Button asChild variant="outline" size="lg">
                        <a 
                          href="https://www.linkedin.com/in/isoline-marchand/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2"
                        >
                          <Linkedin className="w-4 h-4" />
                          {t('about.isolineLinkedIn')}
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Join Beta CTA */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('about.betaTitle')}</h2>
            <p className="text-xl text-muted-foreground mb-8">{t('about.betaSubtitle')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg">
                <Link to="/?scrollTo=demo">Contact Us</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg">
                <Link to="/about">{t('about.betaLearnMore')}</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default About;